import * as React from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { Search } from 'lucide-react'
import { ShortcutHint } from './ShortcutHint'
import type { ShortcutHintProps } from './ShortcutHint'
import { cn } from '@/lib/utils'

type IssueCommandInputProps = React.ComponentPropsWithoutRef<
  typeof CommandPrimitive.Input
> & {
  shortcutHint?: ShortcutHintProps['value']
}

/**
 * A search input component for command palettes with search icon and optional keyboard shortcut hint.
 * Built on top of the CMDK primitive input with consistent styling.
 *
 * @param shortcutHint - Keyboard shortcut to display (string or array of strings)
 * @param className - Additional CSS classes
 * @param ...props - All standard HTML input props
 *
 * @example
 * ```tsx
 * <IssueCommandInput
 *   placeholder="Search tasks..."
 *   shortcutHint={["Cmd", "K"]}
 * />
 * ```
 */
const IssueCommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  IssueCommandInputProps
>(({ className, shortcutHint, ...props }, ref) => (
  <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        'flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
    <ShortcutHint value={shortcutHint} />
  </div>
))

IssueCommandInput.displayName = CommandPrimitive.Input.displayName

export { IssueCommandInput }
